import { Routes, Route, useLocation } from 'react-router-dom';
import React, { useEffect } from 'react';
import { clearSession, useSessionToken } from './auth/session';
import AssistantChat from './components/AssistantChat';
import ProductList from './pages/ProductList';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import PersonalCabinetPage from './pages/PersonalCabinetPage';
import ProtectedRoute from './components/ProtectedRoute';
import LanguageSwitcher from './components/LanguageSwitcher';
import Header from './components/Header';
import Tabs from './tabs/Tabs';
import InventoryPage from './pages/InventoryPage';
import InventoryManagePage from './pages/InventoryManagePage';
import UserAdminPage from './pages/UserAdminPage';
import TransactionHistoryPage from './pages/TransactionHistoryPage';
import AnalyticsPage from './pages/AnalyticsPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
import SubscriptionHistoryPage from './pages/SubscriptionHistoryPage';
import ProductTransferPage from './pages/ProductTransferPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import InactiveAccountPage from './pages/InactiveAccountPage';
import SubscriptionInactivePage from './pages/SubscriptionInactivePage';
import InformationPage from './pages/InformationPage';
import { useSubscriptionStatus } from './hooks/useSubscriptionStatus';
import './tabs/Tabs.css';
import './App.css';
import { useTranslation } from 'react-i18next';

function SubscriptionGate({ children, isAdmin }: { children: React.ReactElement; isAdmin: boolean }) {
  const { t } = useTranslation();
  const subscription = useSubscriptionStatus();

  if (isAdmin) return children;
  if (subscription.isLoading) {
    return <main className="subscription-blocked-page"><div>{t('common.loading')}</div></main>;
  }
  if (!subscription.isActive) return <SubscriptionInactivePage subscription={subscription} />;
  return children;
}

function App() {
  const location = useLocation();
  const { t } = useTranslation();
  const token = useSessionToken();
  useEffect(() => {
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (typeof payload.exp !== 'number') return;
      const remaining = payload.exp * 1000 - Date.now();
      if (remaining <= 0) { clearSession(); return; }
      const timeout = window.setTimeout(clearSession, Math.min(remaining, 2147483647));
      return () => window.clearTimeout(timeout);
    } catch { /* The backend validates malformed tokens. */ }
  }, [token]);
  const role = localStorage.getItem('role');
  const isAdmin = role === 'ROLE_ADMIN';
  const isInventoryOnly = role === 'ROLE_INVENTORY';
  const isTransportOnly = role === 'ROLE_TRANSPORT';
  const transferTab = {
    label: t('navigation.transfers'),
    content: <ProductTransferPage />
  };
  const tabs = isTransportOnly ? [transferTab] : [
    {
      label: t('navigation.inventory'),
      content: <InventoryPage />
    },
    {
      label: t('navigation.inventoryManage'),
      content: <InventoryManagePage />
    },
    transferTab,
  ];

  if (!isInventoryOnly && !isTransportOnly) {
    tabs.unshift({
      label: t('navigation.productTable'),
      content: <ProductList />
    });
    tabs.push({
      label: t('navigation.transactionHistory'),
      content: <TransactionHistoryPage />
    });
    tabs.push({
      label: t('navigation.analytics'),
      content: <AnalyticsPage />
    });
    tabs.push({
      label: t('navigation.personalCabinet'),
      content: <PersonalCabinetPage />
    });
  }

  if (isAdmin) {
    tabs.push({
      label: t('navigation.userManagement'),
      content: <UserAdminPage />
    });
    tabs.push({
      label: t('navigation.subscriptionHistory'),
      content: <SubscriptionHistoryPage />
    });
  }

  tabs.push({
    label: t('navigation.information'),
    content: <InformationPage role={role} />
  });

  const showHeader = Boolean(token) && location.pathname !== '/login' && location.pathname !== '/register'
    && location.pathname !== '/payment/success' && location.pathname !== '/payment/cancel'
    && location.pathname !== '/reset-password';

  return (
    <>
      {showHeader && <Header />}
      {!showHeader && <LanguageSwitcher />}
      <div className={showHeader ? 'app-with-header' : undefined}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/cancel" element={<PaymentCancelPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/account-inactive" element={<InactiveAccountPage />} />
          <Route path="/" element={
            <ProtectedRoute>
              <SubscriptionGate isAdmin={isAdmin}>
                <Tabs key={location.key} tabs={tabs} />
              </SubscriptionGate>
            </ProtectedRoute>
          } />
        </Routes>
      </div>
      {showHeader && location.pathname !== '/account-inactive' && <AssistantChat key={token} />}
    </>
  );
}

export default App;
